import React from 'react';
import { kioskStore, refsStore, layersStore, eventsStore } from '../managers/globalState';
import { Config } from '../config/config';

import '../../www/assets/css/kiosk_mode.css';

class KioskMode extends React.PureComponent {
	constructor(props) {
		super(props);
		const { scene, pioneer } = this.props;

		this.scene = scene;
		this.pioneer = pioneer;

		const { get } = refsStore.stateSnapshot;
		this.get = get;

		this.viewTracker = [{ view: 'spacecraft', viewDuration: 15000 }, { view: 'compare', viewDuration: 8000 }, { view: 'vitalSign', viewDuration: 10000 }];
		this.currentView = null;
		this.autoplayLastClicked = Date.now();
		this.sessionStartTime = Date.now();

		this.cameraManager = this.get('camera');

		this.state = {
			autoplayInProgress: false,
			lastViewVisitedIndex: 0,
			autoplayClickedDuringTransition: false,
			appReset: false,
			startSession: false,
			sessionCountdownTime: Config.sessionMaxTime,
			sessionStartRequested: false,
			showLoading: false
		};

		this.onWindowInteract = this.onWindowInteract.bind(this);
		this.onAutoplayDivClick = this.onAutoplayDivClick.bind(this);
		this.onSessionEndedDivClick = this.onSessionEndedDivClick.bind(this);
	}

	componentDidMount() {
		window.addEventListener('click', this.onWindowInteract);
		window.addEventListener('touchstart', this.onWindowInteract);

		this.autoplayInterval = setInterval(() => {
			const { autoplayClickedDuringTransition, autoplayInProgress, appReset, sessionStartRequested } = this.state;
			const transitionComplete = this.cameraManager.getTransitionComplete();

			// Clicked, waiting for a spacecraft to finish transition before stopping autoplay
			if (autoplayClickedDuringTransition && transitionComplete) {
				this.autoplayLastClicked = Date.now();
				this.setState({ startSession: true });
				this.checkAutoplay(true);
			} else {
				// Update the session timer when not in autoplay mode
				if (!this.checkAutoplay() && this.sessionStartTime && !autoplayInProgress) {
					let sessionTimer = Config.sessionMaxTime - (Date.now() - this.sessionStartTime);

					const { startSession } = this.state;

					if (sessionTimer >= 0) {
						// Session is still ongoing
						this.setState({ sessionCountdownTime: sessionTimer, startSession: false });
					} else if (startSession) {
						// Session restarted
						sessionTimer = Config.sessionMaxTime;
						this.setState({ sessionCountdownTime: Config.sessionMaxTime });
					} else if (sessionStartRequested) {
						// Timer runs out and user hasn't clicked to start over yet
						if (!autoplayInProgress && !appReset) {
							this.resetApp();
						}
						// User clicked to start new session
						if (transitionComplete) {
							// Restart session once in place at visible earth
							this.setState({ sessionCountdownTime: Config.sessionMaxTime, startSession: true });
							this.sessionStartTime = Date.now();
							this.setState({ sessionStartRequested: false });
						} else {
							// Show loading message until transition complete
							this.setState({ showLoading: true });
						}
					}
				}

				this.checkAutoplay();
			}
		}, 1000);
	}

	componentWillUnmount() {
		if (this.autoplayInterval) {
			clearInterval(this.autoplayInterval);
		}

		window.removeEventListener('click', this.onWindowInteract);
		window.removeEventListener('touch', this.onWindowInteract);
	}

	onWindowInteract() {
		// Turn off autoplay if user clicks

		const { autoplayInProgress, lastViewVisitedIndex } = this.state;
		const { view } = this.viewTracker[lastViewVisitedIndex];
		const transitionComplete = this.cameraManager.getTransitionComplete();

		this.cameraManager.setTransitionComplete(false);
		if (autoplayInProgress) {
			this.setState({ autoplayClickedDuringTransition: true });
		}

		// If user is interacting with the screen in normal session, reset last clicked every interaction
		if (!autoplayInProgress) {
			this.autoplayLastClicked = Date.now();
		}

		if (view !== 'spacecraft' || transitionComplete) {
			this.autoplayLastClicked = Date.now();
			this.checkAutoplay(true);
		}

		this.setState({ appReset: false });
	}

	/**
	 * Reset app when session ends
	 * Set time rate to default, hide global placemarks, reset camera
	 */
	async resetApp() {
		const { closeOverlay, setDetailPanelState } = this.props;
		const { autoplayInProgress, sessionCountdownTime } = this.state;
		const sessionTime = new Date(sessionCountdownTime);

		if (this.get('time').getTimeRate() !== this.get('time').getDefaultTimeRate()) {
			this.get('time').setDefaultTime();
		}

		layersStore.setGlobalState({ cityNames: false, groundTracks: false });
		eventsStore.setGlobalState({ showOnGlobe: false });

		// Close overlays and panels
		closeOverlay(false);
		setDetailPanelState(false);

		if (sessionTime.getSeconds() <= 0 && !autoplayInProgress) {
			this.setState({ appReset: true });
			// Reset camera
			// If not at visible earth, nav to
			const navigated = this.get('route').navigate({
				vitalsign: 'visible_earth',
				altid: 0,
				animating: 'f',
				start: '',
				end: '',
				__remove: ['spacecraft', 'altView', 'videoId']
			}, 'vitalsign');

			// If not navigated aka on visible earth already, reset orientation of earth
			if (!navigated) {
				await this.cameraManager.goToEntity(
					'earth',
					{
						latitude: Config.globeLat,
						longitude: Config.globeLong
					}
				);
			}
			this.cameraManager.setTransitionComplete(true);
		}

		this.setState({ showLoading: false });
	}

	/**
	 * Autoplay logic
	 * @param {boolean} stopAutoplay - optional bool to force stop autoplay
	 * @returns
	 */
	checkAutoplay(stopAutoplay = false) {
		const { missionTimedOut, playAutoplay } = kioskStore.stateSnapshot;
		const { autoplayInProgress, lastViewVisitedIndex } = this.state;
		const { setDetailPanelState } = this.props;

		// Reset views if spacecraft times out
		if (missionTimedOut) {
			this.setState({ lastViewVisitedIndex: 2 });
			kioskStore.setGlobalState({ missionTimedOut: false });
		}

		if (!autoplayInProgress) {
			this.viewTracker[lastViewVisitedIndex].startTime = Date.now();
			this.currentView = null;
			this.setState({ lastViewVisitedIndex: 0 });
		}

		// Stop autoplay
		if (stopAutoplay || Date.now() - this.autoplayLastClicked < Config.maxInactivityTIme) {
			this.setState({ autoplayClickedDuringTransition: false });

			if (autoplayInProgress) {
				// Reset autoplay views
				this.setState({ autoplayInProgress: false, lastViewVisitedIndex: 0 });
				this.cameraManager.setTransitionComplete(false);
				this.currentView = null;

				// Nav to home
				this.get('route').navigate({
					vitalsign: 'visible_earth',
					altid: 0,
					animating: 'f',
					start: '',
					end: '',
					__remove: ['spacecraft', 'altView', 'videoId']
				}, 'vitalsign');

				this.setState({ startSession: true });
			}

			this.cameraManager.setTransitionComplete(false);

			return false;
		}

		if (playAutoplay) {
			const { view, viewDuration = 10000, startTime = Date.now() } = this.viewTracker[lastViewVisitedIndex];

			this.setState({ autoplayInProgress: true });
			this.resetApp();

			// Change autoplay views
			if (this.currentView === view) {
				// If we've been at this view longer than its duration, go to next view
				if (Date.now() - startTime > viewDuration) {
					this.setState({ lastViewVisitedIndex: (lastViewVisitedIndex + 1) % this.viewTracker.length });
				}
			} else {
				this.currentView = view;

				const randomMissionId = this._randomMission();

				if (view === 'spacecraft') {
					this._navToMission(randomMissionId);
				} else if (view === 'compare') {
					this._compareObject(randomMissionId);
				} else if (view === 'vitalSign') {
					this._navToVitalSign(this._randomVitalSign());

					// Disable spacecraft and compare object after we nav to vital sign to avoid transition errors
					this.scene.getEntity(randomMissionId)?.setEnabled(false);
					this.scene.getEntity('school_bus')?.setEnabled(false);

					this.cameraManager.setTransitionComplete(false);
				}

				this.viewTracker[lastViewVisitedIndex].startTime = Date.now();
			}
		}


		setDetailPanelState(false);
	}

	/**
	 * Get random mission id - excludes instruments
	 * @returns string of random mission id
	 */
	_randomMission() {
		const { missionList } = this.get('content');

		const missionListNoInstruments = missionList.filter(mission => {
			const missionId = this.get('content').getMissionId(mission?.external_id);
			const { instrument } = this.get('content').getEntityInfo(missionId) || {};
			return instrument === undefined;
		});

		const randomNum = Math.floor(Math.random(0, missionListNoInstruments.length) * missionListNoInstruments.length + 1);
		const missionId = this.get('content').getMissionId(missionListNoInstruments[randomNum]?.external_id);

		const firstMissionInList = this.get('content').getMissionId(missionListNoInstruments[0]?.external_id);

		// Return safety spacecraft id if missionId is null
		return missionId || firstMissionInList;
	}

	/**
	 * Get random vital sign from vitals list
	 * @returns string denoting a vital sign
	 */
	_randomVitalSign() {
		const { vitalList } = this.get('content');

		const randomNum = Math.floor(Math.random(0, vitalList.length) * vitalList.length + 1);
		const vitalSign = vitalList[randomNum]?.value;
		return vitalSign || 'visible_earth';
	}

	/**
	 * Navigate to a mission
	 * @param {string} missionId
	 */
	_navToMission(missionId) {
		this.get('route').navigate({
			spacecraft: missionId,
			vitalsign: 'satellites',
			__remove: ['altid', 'animating', 'start', 'end', 'videoId']
		}, 'spacecraft');
	}

	/**
	 * Display comparison object next to spacecraft
	 * @param {string} missionId
	 */
	async _compareObject(missionId) {
		const missionEntity = this.scene.getEntity(missionId);
		await missionEntity?.getLoadedPromise().then(() => {
			this.get('comparison').updateCompareObject('School Bus');
		});

		await this.pioneer.waitUntilNextFrame();
	}

	/**
	 * Navigate to vital sign
	 * @param {string} vitalSign
	 */
	_navToVitalSign(vitalSign) {
		this.get('route').navigate({
			vitalsign: vitalSign,
			altid: 0,
			animating: 'f',
			start: '',
			end: '',
			__remove: ['spacecraft', 'altView', 'videoId']
		}, 'vitalsign');
	}

	/**
	 * @param {number} incomingTime
	 * @returns {object} sec, min, hour in the form {sec: 0, min: 0, hour: 0}
	 */
	getSecMinHours(incomingTime) {
		let sec = Math.floor(incomingTime / 1000);
		let min = Math.floor(sec / 60);
		const hour = Math.floor(min / 60);

		sec %= 60;
		min %= 60;

		return { sec, min, hour };
	}

	/**
	 * Format time into hh:mm:ss for session timer
	 * @param {number} incomingTime
	 */
	formatTime(incomingTime) {
		// One hour in milliseconds
		const oneHour = 60 * 60 * 1000;
		const time = this.getSecMinHours(incomingTime);

		// Pad the front with '0' until it hits 2 digits
		time.hour = time.hour.toString().padStart(2, '0');
		time.min = time.min.toString().padStart(2, '0');
		time.sec = time.sec.toString().padStart(2, '0');

		return `${Config.sessionMaxTime > oneHour ? time.hour + ':' : ''}${time.min || '00'}:${time.sec}`;
	}

	onAutoplayDivClick() {
		this.sessionStartTime = Date.now();
	}

	onSessionEndedDivClick() {
		// Restart session
		this.setState({ sessionStartRequested: true });
	}

	render() {
		const { autoplayInProgress, sessionCountdownTime, autoplayClickedDuringTransition, showLoading, startSession, sessionStartRequested } = this.state;
		const time = this.getSecMinHours(sessionCountdownTime);
		const sessionEnded = time.hour === 0 && time.min === 0 && time.sec === 0;

		return (
			autoplayInProgress
				? (
					<div className='autoplay-main' onPointerDown={this.onAutoplayDivClick}>
						{autoplayClickedDuringTransition
							?
								<span className='starting-text'>
									Starting experience...
								</span>
							:
								<div className='autoplay-button'>
									TOUCH TO START
								</div>}

					</div>
				)
				:
				(
					<div className={`session-timer-container-${sessionEnded && !startSession ? 'session-over' : 'counting-down'}`} onPointerDown={this.onSessionEndedDivClick}>
						{!sessionEnded && (
							<div>
								Time left:
								{' '}
								{this.formatTime(sessionCountdownTime)}
							</div>
						)}
						{sessionEnded && !startSession
						&& (showLoading && sessionStartRequested ?
							<span className='starting-text'>
								Starting experience...
							</span> :
							(!sessionStartRequested
							&& (
								<div>
									<div>Your session time is up</div>
									<div className='session-ended-button'>START OVER</div>
								</div>
							)
							)
						)}

					</div>
				)
		);
	}
}

export default KioskMode;
